<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

import type { CreatePrefill } from "@/entities/client"
import { toast } from "@/shared/lib"
import { Button, FormAlert, Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/shared/ui"

import type { MappedBookingError } from "../model/mapBookingError"
import { useKeyboardInset } from "../model/useKeyboardInset"
import { resolveStartSlot } from "../model/nearestSlot"
import { useRecordsStore } from "../model/store"
import { useCreateBookingForm } from "../model/useCreateBookingForm"
import BookingDateTimePicker from "./BookingDateTimePicker.vue"
import ClientPicker from "./ClientPicker.vue"
import InlineClientCreate from "./InlineClientCreate.vue"

defineOptions({ name: "CreateBookingSheet" })

function minuteLabel(min: number): string {
  const h = Math.floor(min / 60) % 24
  const m = min % 60
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

const store = useRecordsStore()
const { t, d } = useI18n()
const { keyboardInset, viewportHeight } = useKeyboardInset()

const open = computed(() => store.activeSheet === "create")
const form = useCreateBookingForm()

const clientMode = ref<"pick" | "create">("pick")
const inlinePrefill = ref<CreatePrefill>({})

const activeMaster = computed(
  () => store.workingMasters.find((m) => m.id === store.activeMasterId) ?? null,
)
const masterName = computed(() => activeMaster.value?.name ?? "—")

const dateLabel = computed(() => {
  const [year, month, day] = store.selectedDate.split("-").map(Number)
  const dt = new Date(year, month - 1, day)
  return d(dt, { month: "long", day: "numeric" })
})

const serviceOptions = computed(() => {
  if (!activeMaster.value) return []
  return store.services.filter((s) => activeMaster.value!.serviceIds.includes(s.id))
})

const INLINE_KEYS = new Set<string>([
  "journal.errors.slotTaken",
  "journal.errors.outsideWorkingHours",
  "journal.errors.serviceNotConfigured",
  "journal.errors.invalidPhone",
  "journal.errors.invalidClientName",
])

const generalError = computed(() => {
  const key = store.mutationErrorKey
  return key && INLINE_KEYS.has(key) ? t(key) : undefined
})

const announceText = ref("")

watch(open, (isOpen) => {
  if (!isOpen) return
  form.reset()
  clientMode.value = "pick"
  inlinePrefill.value = {}
  announceText.value = ""
})

// S3: preselect слоту — лише коли availability стала 'ready'
watch(
  () => store.availabilityStatus,
  (status) => {
    if (status !== "ready") return
    if (!form.selectedServiceId.value) return
    const result = resolveStartSlot(
      store.availabilitySlots,
      store.salonTz,
      store.createStartMin,
    )
    form.selectedSlot.value = result.slot
    form.nearestHint.value = result.hint
  },
)

watch(
  () => form.selectedServiceId.value,
  (serviceId) => {
    form.selectedSlot.value = null
    form.nearestHint.value = null
    if (serviceId && store.activeMasterId !== "all") {
      void store.loadAvailability(store.activeMasterId, serviceId, form.localDate.value)
    }
  },
)

function handleOpenChange(isOpen: boolean): void {
  if (isOpen) return
  if (store.mutationStatus === "pending") return
  store.closeSheet()
}

function handleRequestCreate(prefill: CreatePrefill): void {
  inlinePrefill.value = prefill
  clientMode.value = "create"
}

async function handleSubmit(): Promise<void> {
  if (!form.canSubmit.value) return

  const input = form.buildInput()

  try {
    await store.submitCreate(input)
    announceText.value = t("journal.a11y.createdAnnounce")
  } catch (err) {
    const mapped = err as MappedBookingError
    if (mapped.surface === "toast") {
      if (mapped.toastVariant === "info") {
        toast.info(t(mapped.i18nKey))
      } else {
        toast.error(t(mapped.i18nKey))
      }
    }
    if (mapped.reloadAvailability && store.activeMasterId !== "all" && form.selectedServiceId.value) {
      form.selectedSlot.value = null
      void store.loadAvailability(store.activeMasterId, form.selectedServiceId.value, form.localDate.value)
    }
  }
}
</script>

<template>
  <span aria-live="polite" class="sr-only">{{ announceText }}</span>

  <Sheet :open="open" @update:open="handleOpenChange">
    <SheetContent
      side="bottom"
      :close-aria-label="t('journal.create.close')"
      :style="keyboardInset > 0 ? { bottom: `${keyboardInset}px`, maxHeight: `${viewportHeight}px` } : undefined"
    >
      <SheetHeader>
        <SheetTitle>{{ t("journal.create.title") }}</SheetTitle>
      </SheetHeader>

      <form
        class="create-sheet__form"
        data-testid="create-form"
        @submit.prevent="handleSubmit"
      >
        <div class="create-sheet__scroll">
          <div class="create-sheet__context">
            <span aria-hidden="true">🔒</span>
            <span>{{ masterName }} · {{ dateLabel }}</span>
          </div>

          <FormAlert
            v-if="generalError"
            data-testid="form-error"
            variant="destructive"
            :message="generalError"
          />

          <!-- Секція 1: Клієнт -->
          <div class="create-sheet__section" data-testid="section-client">
            <div class="create-sheet__section-header">
              <span class="create-sheet__section-num" aria-hidden="true">1</span>
              <span class="create-sheet__section-label">{{ t("records.createFlow.sections.client") }}</span>
            </div>

            <div v-if="form.selectedClient.value" class="create-sheet__summary">
              <span>{{ form.clientName.value }}</span>
              <button
                type="button"
                class="create-sheet__change-btn"
                @click="form.clearClient()"
              >
                {{ t("records.createFlow.summary.change") }}
              </button>
            </div>

            <template v-else>
              <ClientPicker
                v-if="clientMode === 'pick'"
                :model-value="form.selectedClient.value"
                @update:model-value="form.pickClient($event)"
                @request-create="handleRequestCreate"
              />
              <InlineClientCreate
                v-else
                :prefill="inlinePrefill"
                @created="(c) => { form.applyInlineCreated(c); clientMode = 'pick' }"
                @back="clientMode = 'pick'"
              />
            </template>
          </div>

          <!-- Секція 2: Послуга -->
          <div
            class="create-sheet__section"
            :class="{ 'create-sheet__section--locked': form.step.value === 'client' }"
            :aria-disabled="form.step.value === 'client' ? 'true' : undefined"
            data-testid="section-service"
          >
            <div class="create-sheet__section-header">
              <span class="create-sheet__section-num" aria-hidden="true">2</span>
              <span class="create-sheet__section-label">{{ t("records.createFlow.sections.service") }}</span>
            </div>

            <div v-if="form.selectedServiceId.value" class="create-sheet__summary">
              <span>{{ store.services.find((s) => s.id === form.selectedServiceId.value)?.name }}</span>
              <button
                type="button"
                class="create-sheet__change-btn"
                @click="form.selectedServiceId.value = ''"
              >
                {{ t("records.createFlow.summary.change") }}
              </button>
            </div>

            <ul
              v-else-if="form.step.value !== 'client'"
              class="create-sheet__service-list"
              role="listbox"
              :aria-label="t('records.createFlow.sections.service')"
            >
              <li v-for="service in serviceOptions" :key="service.id" role="option">
                <button
                  type="button"
                  class="create-sheet__service-row"
                  :data-testid="`service-row-${service.id}`"
                  @click="form.selectedServiceId.value = service.id"
                >
                  {{ service.name }}
                </button>
              </li>
            </ul>
          </div>

          <!-- Секція 3: Час -->
          <div
            class="create-sheet__section"
            :class="{ 'create-sheet__section--locked': form.step.value !== 'time' }"
            :aria-disabled="form.step.value !== 'time' ? 'true' : undefined"
            data-testid="section-time"
          >
            <div class="create-sheet__section-header">
              <span class="create-sheet__section-num" aria-hidden="true">3</span>
              <span class="create-sheet__section-label">{{ t("records.createFlow.sections.time") }}</span>
            </div>

            <template v-if="form.step.value === 'time'">
              <p
                v-if="form.nearestHint.value"
                class="create-sheet__nearest-hint"
                data-testid="nearest-hint"
              >
                {{ t("records.createFlow.time.nearestHint", {
                  tapped: minuteLabel(form.nearestHint.value.tappedMin),
                  chosen: minuteLabel(form.nearestHint.value.chosenMin),
                }) }}
              </p>

              <BookingDateTimePicker
                :model-value="form.selectedSlot.value"
                :master-id="store.activeMasterId"
                :service-id="form.selectedServiceId.value"
                :date="form.localDate.value"
                :tz="store.salonTz"
                @update:model-value="form.selectedSlot.value = $event"
                @update:date="form.localDate.value = $event"
              />
            </template>
          </div>
        </div>

        <SheetFooter class="create-sheet__footer">
          <Button
            type="submit"
            class="w-full"
            data-testid="btn-submit"
            :loading="store.mutationStatus === 'pending'"
            :disabled="!form.canSubmit.value"
          >
            {{ t("journal.create.submit") }}
          </Button>
        </SheetFooter>
      </form>
    </SheetContent>
  </Sheet>
</template>

<style scoped>
.create-sheet__form {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.create-sheet__scroll {
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-y: auto;
  padding: 16px;
  flex: 1;
}

.create-sheet__context {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: var(--muted-foreground);
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 12px;
}

.create-sheet__section {
  padding: 12px 0;
  border-bottom: 1px solid var(--border);
}

.create-sheet__section--locked {
  opacity: 0.45;
  pointer-events: none;
}

.create-sheet__section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.create-sheet__section-num {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.create-sheet__section-label {
  font-size: 0.9375rem;
  font-weight: 600;
}

.create-sheet__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.875rem;
}

.create-sheet__change-btn {
  font-size: 0.8125rem;
  color: var(--primary);
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
}

.create-sheet__change-btn:focus-visible {
  outline: 2px solid var(--ring);
  border-radius: 2px;
}

.create-sheet__service-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.create-sheet__service-row {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  text-align: left;
  font-size: 0.875rem;
}

.create-sheet__service-row:last-child {
  border-bottom: none;
}

.create-sheet__service-row:hover {
  background: var(--accent);
}

.create-sheet__service-row:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: -2px;
}

.create-sheet__nearest-hint {
  font-size: 0.8125rem;
  color: var(--info-foreground, var(--muted-foreground));
  background: var(--info-subtle, var(--muted));
  border-radius: var(--radius);
  padding: 6px 10px;
  margin-bottom: 8px;
}

.create-sheet__footer {
  border-top: 1px solid var(--border);
  padding: 12px 16px;
}
</style>
